import { useState, useEffect, useCallback } from 'react';
import api, { urlDocument } from 'services/api';
import ImageProtegee from 'components/ui/ImageProtegee';
import useEscapeClose from 'hooks/useEscapeClose';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';
import { preparerFichier } from 'services/televersement';
/* 🔴 `useAuth` / `canAction` REVIENNENT — et la note qu ils remplacent etait
 *   FAUSSE DEPUIS LE 16/09/2026. Elle affirmait << cette page ne porte plus aucun
 *   acte reserve >> : le bouton de suppression etait TOUJOURS la, et la route
 *   `DELETE /agents/:id` est gardee par `canDelete` = admin, dg, rh. Il etait
 *   donc montre aux 8 roles de `canRead` et refuse a 5 — un 403 au clic.
 *   ⓘ `canAction(roles,'agents','delete')` est vrai exactement pour dg, admin,
 *     rh (`permissions.js`) : la garde d ecran EPOUSE la garde de route, elle ne
 *     la devine pas. *Un bouton visible sur une route fermee est une promesse que
 *     le serveur ne tient pas — le miroir du defaut deja nomme dans
 *     `agentRoutes.js`.* */
import { useAuth } from 'hooks/useAuth';
import { canAction } from 'permissions';

const STATUT_COLORS = { actif: '#2e7d32', suspendu: '#f57c00', inactif: '#9e9e9e', en_conge: '#1565c0' };
const STATUT_LABELS = { actif: 'Actif', suspendu: 'Suspendu', inactif: 'Inactif', en_conge: 'En conge' };

/* ⓘ JJ/MM/AAAA, sans dependance : `toLocaleDateString` suit la langue du
 *   NAVIGATEUR, pas celle du produit — un poste en anglais aurait affiche
 *   MM/JJ/AAAA, et « Sorti le 09/10 » se serait lu « 10 septembre ». */
const jourFr = (d) => {
  if (!d) return '';
  const [a, m, j] = String(d).slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
};

const EMPTY = {
  nom: '', prenom: '', telephone: '', email: '', sexe: '', ville: '', adresse: '',
  type_contrat: 'CDI', salaire_base: '', date_embauche: '', date_naissance: '',
  specialite: '', contact_urgence_nom: '', contact_urgence_tel: '',
  date_fin_contrat: '', date_fin_essai: '', essai_renouvele: false,
};

export default function Agents() {
  const { user } = useAuth();
  const peutPoserUneSortie = canAction(user?.roles, 'agents', 'delete');
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ total: 0, actif: 0, suspendu: 0, inactif: 0, en_conge: 0, sortis: 0 });
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Constat rendu par le serveur apres une correction de date d embauche.
  const [constat, setConstat] = useState('');
  /* Sortie d effectif — l agent vise, le formulaire, et ce que le serveur rend.
   * `sortieAlerte` porte la phrase du serveur TELLE QUELLE : c est lui qui sait
   * si le motif exige une procedure formelle, pas cet ecran. */
  const [sortie, setSortie] = useState(null);
  const [sortieForm, setSortieForm] = useState({ date_sortie: '', motif_sortie_id: '' });
  const [motifs, setMotifs] = useState([]);
  const [sortieErreur, setSortieErreur] = useState('');
  const [sortieAlerte, setSortieAlerte] = useState('');
  const [sortieEnCours, setSortieEnCours] = useState(false);
  /* Correction d une sortie erronee. `correction.mode` vaut 'modifier' ou
   * 'annuler' : le MEME formulaire porte les deux, parce que le serveur n a
   * qu un point d ecriture. */
  const [correction, setCorrection] = useState(null);
  const [corrForm, setCorrForm] = useState({ mode: 'modifier', date_sortie: '', motif_sortie_id: '', motif_correction: '' });
  const [corrErreur, setCorrErreur] = useState('');
  const [corrAlerte, setCorrAlerte] = useState('');
  const [corrEnCours, setCorrEnCours] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [agentsRes, statsRes] = await Promise.all([
        api.get('/agents', { params: { search: search || undefined } }),
        api.get('/agents/stats'),
      ]);
      setAgents(agentsRes.data);
      setStats(statsRes.data);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  /* ⚠️ La limite N'EST PLUS ÉCRITE ICI (04/09/2026). Avant :
   *    `file.size > 2 * 1024 * 1024` et « max 2 MB » en dur — une copie de plus
   *    de la limite, indépendante de multer et de Nginx (qui coupait à 1 Mo).
   *    Désormais : la table `photo_agent`, redimensionnement navigateur compris.
   *    Le message nomme le poids REÇU et la limite. */
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const { fichier, erreur } = await preparerFichier(file, 'photo_agent');
    if (erreur) { setError(erreur); e.target.value = ''; return; }
    setError('');
    setPhoto(fichier);
    setPhotoPreview(URL.createObjectURL(fichier));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId && !photo) { setError('La photo est obligatoire'); return; }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null) formData.append(k, v); });
      if (photo) formData.append('photo', photo);

      if (editId) {
        /* Le serveur peut rendre un CONSTAT avec la fiche — aujourd hui celui
         * des bulletins deja emis quand la date d embauche change (16/09/2026).
         * On l affiche tel quel : c est un fait, pas une consigne (regle 62). */
        const { data } = await api.put(`/agents/${editId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data && data.constats && data.constats.length > 0) {
          setConstat(data.constats.map(c => c.message).join(' '));
        }
      } else {
        await api.post('/agents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      closeForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const closeForm = () => {
    setShowForm(false); setForm(EMPTY); setEditId(null);
    setPhoto(null); setPhotoPreview(null);
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/agents/${id}`);
      setDetail(res.data);
    } catch { setError('Erreur de chargement'); }
  };

  const handleEdit = (agent) => {
    setForm({
      nom: agent.nom, prenom: agent.prenom, telephone: agent.telephone || '',
      email: agent.email || '', sexe: agent.sexe || '', ville: agent.ville || '',
      adresse: agent.adresse || '', type_contrat: agent.type_contrat || 'CDI',
      salaire_base: agent.salaire_base || '', date_embauche: agent.date_embauche?.split('T')[0] || '',
      date_naissance: agent.date_naissance?.split('T')[0] || '', specialite: agent.specialite || '',
      contact_urgence_nom: agent.contact_urgence_nom || '', contact_urgence_tel: agent.contact_urgence_tel || '',
      date_fin_contrat: agent.date_fin_contrat?.split('T')[0] || '',
      date_fin_essai: agent.date_fin_essai?.split('T')[0] || '',
      essai_renouvele: agent.essai_renouvele || false, statut: agent.statut,
    });
    setPhotoPreview(agent.photo_url ? urlDocument('photo-agent', agent.id) : null);
    setEditId(agent.id);
    setDetail(null);
    setShowForm(true);
  };

  /* 🔴 CE QUI REMPLACE `handleDelete`, ET POURQUOI IL DISPARAIT.
   *   L ancien appelait `api.delete('/agents/:id')` SANS CORPS, sous un
   *   `window.confirm('Desactiver cet agent ?')`. Trois defauts en trois lignes :
   *   ① le serveur exige `date_sortie` ET `motif_sortie_id` depuis le
   *      04/09/2026 -> **400 systematique en production**, jamais rien ecrit ;
   *   ② << Desactiver >> nommait un STATUT alors que le serveur pose une SORTIE :
   *      le mot de l ecran et l acte du serveur ne parlaient pas de la meme chose ;
   *   ③ `catch { setError('Erreur') }` ecrasait la phrase du serveur — l ecran
   *      disait << Erreur >> la ou le serveur disait ce qui manquait.
   *   ⚠️ LA FICHE N EST JAMAIS SUPPRIMEE : historique de paie, attestations,
   *      obligation de conservation. Elle sort du COMPTE, pas de la base. */
  const ouvrirSortie = async (agent) => {
    setSortie(agent);
    setSortieForm({ date_sortie: '', motif_sortie_id: '' });
    setSortieErreur(''); setSortieAlerte('');
    try {
      const { data } = await api.get('/agents/motifs-sortie');
      setMotifs(data.motifs || []);
    } catch (err) {
      /* Pas de repli sur une liste en dur : sans referentiel on ne propose RIEN
       * et on le DIT, avec la phrase du serveur. Un motif devine serait refuse. */
      setMotifs([]);
      setSortieErreur(err?.response?.data?.error
        || 'Les motifs de sortie n’ont pas pu être lus. La sortie ne peut pas être enregistrée.');
    }
  };

  const enregistrerSortie = async (e) => {
    e.preventDefault();
    setSortieErreur(''); setSortieAlerte(''); setSortieEnCours(true);
    try {
      const { data } = await api.delete(`/agents/${sortie.id}`, {
        data: { date_sortie: sortieForm.date_sortie, motif_sortie_id: sortieForm.motif_sortie_id },
      });
      /* ⚠️ L ALERTE NE FERME PAS LA MODALE. Le serveur ALERTE sans bloquer quand
       *   le motif exige une procedure formelle ; refermer l ecran ferait
       *   disparaitre la phrase avant qu elle soit lue. */
      load();
      if (data.alerte) setSortieAlerte(data.alerte);
      else setSortie(null);
    } catch (err) {
      /* La phrase du SERVEUR, telle quelle — jamais << Erreur >>. Il sait dire
       * << Motif ... inconnu ou non applicable dans le droit de ce pays >>. */
      setSortieErreur(err?.response?.data?.error
        || 'La sortie n’a pas pu être enregistrée.');
    } finally { setSortieEnCours(false); }
  };

  const handleValiderModule = async (formationId) => {
    try {
      await api.put(`/formations/valider/${formationId}`, { validee_par: 'Superviseur' });
      openDetail(detail.id);
    } catch { setError('Erreur de validation'); }
  };

  const handleAssignModule = async (moduleId) => {
    try {
      await api.post('/formations/assign', { agent_id: detail.id, module_id: moduleId });
      openDetail(detail.id);
    } catch { setError('Erreur d\'assignation'); }
  };

  /* ⚠️ LE MOTIF DE CORRECTION N EST PAS LE MOTIF DE SORTIE. L un dit pourquoi
   *   la personne est partie, l autre pourquoi la trace change. L ecran les
   *   presente donc separement, et n en preremplit AUCUN des deux. */
  const ouvrirCorrection = async (agent) => {
    setCorrection(agent);
    setCorrForm({
      mode: 'modifier',
      date_sortie: String(agent.date_sortie || '').slice(0, 10),
      motif_sortie_id: agent.motif_sortie_id || '',
      motif_correction: '',
    });
    setCorrErreur(''); setCorrAlerte('');
    try {
      const { data } = await api.get('/agents/motifs-sortie');
      setMotifs(data.motifs || []);
    } catch (err) {
      setMotifs([]);
      setCorrErreur(err?.response?.data?.error
        || 'Les motifs de sortie n’ont pas pu être lus.');
    }
  };

  const enregistrerCorrection = async (e) => {
    e.preventDefault();
    setCorrErreur(''); setCorrAlerte(''); setCorrEnCours(true);
    const annule = corrForm.mode === 'annuler';
    try {
      const { data } = await api.patch(`/agents/${correction.id}/sortie`, {
        date_sortie: annule ? null : corrForm.date_sortie,
        motif_sortie_id: annule ? null : corrForm.motif_sortie_id,
        motif_correction: corrForm.motif_correction,
      });
      /* ⚠️ L ALERTE NE FERME PAS LA MODALE : c est elle qui porte
       *   « Régularisation à prévoir sur la paie suivante ». La refermer ferait
       *   disparaitre la seule phrase qui compte. */
      load();
      if (data.alerte) setCorrAlerte(data.alerte);
      else setCorrection(null);
    } catch (err) {
      setCorrErreur(err?.response?.data?.error
        || 'La correction n’a pas pu être enregistrée.');
    } finally { setCorrEnCours(false); }
  };

  useEscapeClose(showForm, closeForm);
  useEscapeClose(detail && !showForm, () => setDetail(null));
  useEscapeClose(!!sortie, () => setSortie(null));
  useEscapeClose(!!correction, () => setCorrection(null));

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const showCDD = form.type_contrat === 'CDD';
  const showEssai = form.type_contrat === 'Periode_essai';

  return (
    <div>
<div className="stats-grid">
        {[
          { label: 'Total', value: stats.total, cls: 'red' },
          { label: 'Actifs', value: stats.actif, cls: 'green' },
          { label: 'En conge', value: stats.en_conge, cls: 'blue' },
          { label: 'Suspendus', value: stats.suspendu, cls: 'orange' },
          /* ⓘ LA DONNEE ETAIT DEJA SERVIE ET JETEE. `agentController.stats`
           *   rend `sortis` depuis le 24/09/2026 — via
           *   `gladiator_sec.sortis_sur_periode`, la MEME source que l effectif —
           *   et cet ecran n affichait que quatre tuiles. *Une donnee servie que
           *   personne n affiche ne corrige rien.*
           * ⚠️ Les tuiles de statut EXCLUENT deja les sortis cote serveur
           *   (`date_sortie IS NULL`) : « Actifs » et « Sortis » ne se recouvrent
           *   pas, ils se completent. */
          { label: 'Sortis', value: stats.sortis, cls: 'grey' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header">
              <span className="stat-card-label">{s.label}</span>
              <div className={`stat-card-icon ${s.cls}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </div>
            </div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}
      {constat && <div className="gss-alert" style={{ background: '#eef4fb', border: '1px solid #c5d9ee', color: '#2c4a6b' }}>{constat}<button onClick={() => setConstat('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#2c4a6b' }}>&times;</button></div>}

      <div className="content-card">
        <div className="gss-toolbar">
          <input className="gss-input" placeholder="Rechercher un agent..." value={search} onChange={e => setSearch(e.target.value)} />
          {/* 🔴 AUCUN BOUTON, AUCUNE INVITATION A CREER UN AGENT ICI —
            * POUR TOUT LE MONDE, y compris admin, dg et rh.
            *
            * « La permission est invisible, l emplacement est ce que les gens
            *   lisent. Un menu est une carte des responsabilites : si les
            *   Operations proposent de creer un agent, l ecran dit que les
            *   Operations embauchent, et c est faux. » — 16/09/2026
            *
            * ⚠️ CE N EST PLUS UNE QUESTION DE PERMISSION, ET C EST TOUT L OBJET
            *    DU LOT. Le bouton etait deja sous `canAction(... 'create')` :
            *    la permission etait JUSTE, et il s affichait quand meme pour
            *    rh/dg/admin. Un garde de role ne corrige pas un defaut de
            *    carte — il place l acte au bon endroit pour les uns et le
            *    laisse au mauvais pour les autres.
            * ⓘ LES OPERATIONS LISENT LA LISTE : elles en ont besoin pour
            *   affecter. Elles ne creent jamais. La creation vit sous
            *   Ressources humaines, et nulle part ailleurs.
            * ⓘ Rien d autre ne bouge : ni la permission, ni le formulaire, ni
            *   le validateur, ni les routes (decision de Dom). */}
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : agents.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            {/* ⓘ L INVITATION « Cliquez sur le bouton pour commencer » EST
              *   RETIREE : elle designait un bouton qui n existe plus ici, et
              *   elle invitait a creer depuis les Operations.
              * ⚠️ ON NE LA REMPLACE PAS PAR « creez depuis Ressources
              *    humaines » : le produit CONSTATE, il n ordonne pas et
              *    n explique pas ses restrictions (regles 26 et 62). L ecran
              *    dit ce qui est, la carte du menu dit ou l on va. */}
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun agent enregistré</div>
          </div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th></th><th>Matricule</th><th>Nom</th><th>Prénom</th><th>Spécialité</th><th>Téléphone</th><th>Contrat</th><th>Statut</th><th style={{ width: 90 }}>Actions</th></tr>
              </thead>
              <tbody>
                {agents.map(a => (
                  <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(a.id)}>
                    <td>
                      {a.photo_url ? (
                        <ImageProtegee src={urlDocument('photo-agent', a.id)} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--gss-red)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>
                          {a.prenom?.[0]}{a.nom?.[0]}
                        </div>
                      )}
                    </td>
                    <td><strong>{a.matricule}</strong></td>
                    <td>{a.nom}</td>
                    <td>{a.prenom}</td>
                    <td>{a.specialite || '-'}</td>
                    <td>{a.telephone || '-'}</td>
                    <td>{a.type_contrat}</td>
                    <td>
                      <span className="gss-badge" style={{ background: STATUT_COLORS[a.statut] + '20', color: STATUT_COLORS[a.statut] }}>
                        {STATUT_LABELS[a.statut]}
                      </span>
                      {/* ⚠️ LA SORTIE S AJOUTE AU STATUT, ELLE NE LE REMPLACE PAS.
                        *   Une sortie n est pas un statut — c est la decision du
                        *   04/09/2026, et l ecran la rend telle quelle : la
                        *   pastille de statut reste ce qu elle etait, la sortie
                        *   se lit a cote. *Ecraser le statut ici aurait refait, a
                        *   l ecran, la confusion qu on a defaite au serveur.* */}
                      {a.date_sortie && (
                        <span className="gss-badge" title="La fiche est conservée"
                              style={{ background: '#9e9e9e20', color: '#5f5f5f', marginLeft: 6 }}>
                          Sorti le {jourFr(a.date_sortie)}
                        </span>
                      )}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" onClick={() => handleEdit(a)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {/* ⚠️ L ICONE POUBELLE DISPARAIT AVEC LE GESTE. Garder une
                          *   poubelle pour poser une sortie ferait croire a une
                          *   suppression — or la fiche reste. Le signe suit l acte.
                          * ⓘ Un agent DEJA sorti n a pas ce bouton : la sortie se
                          *   pose une fois. Reprendre une sortie erronee n est PAS
                          *   dans ce lot — ce geste n existe pas encore. */}
                        {/* ⓘ Les deux boutons S EXCLUENT : on pose une sortie, ou
                          *   on corrige celle qui existe. Jamais les deux — le
                          *   serveur refuse d ailleurs l un et l autre a contretemps
                          *   (`AUCUNE_SORTIE_A_CORRIGER`). */}
                        {peutPoserUneSortie && a.date_sortie && (
                          <button className="gss-btn-icon" onClick={() => ouvrirCorrection(a)}
                                  title="Corriger la sortie">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 9 8 9"/>
                            </svg>
                          </button>
                        )}
                        {peutPoserUneSortie && !a.date_sortie && (
                          <button className="gss-btn-icon" onClick={() => ouvrirSortie(a)}
                                  title="Enregistrer une sortie">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                              <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── FICHE AGENT DETAIL ── */}
      {detail && (
        <div className="gss-modal-overlay" onClick={() => setDetail(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {detail.photo_url ? (
                  <ImageProtegee src={urlDocument('photo-agent', detail.id)} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--gss-red)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    {detail.prenom?.[0]}{detail.nom?.[0]}
                  </div>
                )}
                <div>
                  <h2>{detail.prenom} {detail.nom}</h2>
                  <span style={{ fontSize: '0.8rem', color: 'var(--gss-text-light)' }}>{detail.matricule} — {detail.specialite || detail.type_contrat}</span>
                </div>
              </div>
              <button className="gss-modal-close" onClick={() => setDetail(null)}>&times;</button>
            </div>

            <div className="gss-detail-grid">
              <div className="gss-detail-item"><label>Téléphone</label><span>{detail.telephone || '-'}</span></div>
              <div className="gss-detail-item"><label>Email</label><span>{detail.email || '-'}</span></div>
              <div className="gss-detail-item"><label>Date naissance</label><span>{detail.date_naissance?.split('T')[0] || '-'}</span></div>
              <div className="gss-detail-item"><label>Sexe</label><span>{detail.sexe === 'M' ? 'Masculin' : detail.sexe === 'F' ? 'Feminin' : '-'}</span></div>
              <div className="gss-detail-item"><label>Ville</label><span>{detail.ville || '-'}</span></div>
              <div className="gss-detail-item"><label>Adresse</label><span>{detail.adresse || '-'}</span></div>
              <div className="gss-detail-item"><label>Type contrat</label><span>{detail.type_contrat}</span></div>
              <div className="gss-detail-item"><label>Date embauche</label><span>{detail.date_embauche?.split('T')[0] || '-'}</span></div>
              <div className="gss-detail-item"><label>Salaire base</label><span>{detail.salaire_base ? Number(detail.salaire_base).toLocaleString() + ' FCFA' : '-'}</span></div>
              <div className="gss-detail-item"><label>Contact urgence</label><span>{detail.contact_urgence_nom ? `${detail.contact_urgence_nom} (${detail.contact_urgence_tel})` : '-'}</span></div>
              <div className="gss-detail-item"><label>Statut</label>
                <span className="gss-badge" style={{ background: STATUT_COLORS[detail.statut] + '20', color: STATUT_COLORS[detail.statut] }}>
                  {STATUT_LABELS[detail.statut]}
                </span>
              </div>
              <div className="gss-detail-item"><label>Badge QR</label><span style={{ fontSize: '0.75rem' }}>{detail.badge_qr_code || '-'}</span></div>
            </div>

            {/* ── FORMATIONS ── */}
            <div style={{ padding: '0 1.5rem 1rem' }}>
              <div className="content-card-title">Parcours de formation</div>
              {detail.modules_disponibles && detail.modules_disponibles.length > 0 ? (
                <div>
                  {(() => {
                    const obligatoires = detail.modules_disponibles.filter(m => m.obligatoire);
                    const formationsMap = {};
                    (detail.formations || []).forEach(f => { formationsMap[f.module_id] = f; });
                    const valides = obligatoires.filter(m => formationsMap[m.id]?.validee).length;
                    return (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${obligatoires.length > 0 ? (valides / obligatoires.length) * 100 : 0}%`, height: '100%', background: valides === obligatoires.length ? '#2e7d32' : '#f57c00', borderRadius: 4, transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{valides}/{obligatoires.length}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <table className="gss-table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr><th>Module</th><th>Type</th><th>Statut</th><th>Date validation</th><th>Valide par</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {detail.modules_disponibles.map(mod => {
                        const fa = (detail.formations || []).find(f => f.module_id === mod.id);
                        return (
                          <tr key={mod.id}>
                            <td>
                              <strong>{mod.titre}</strong>
                              {!mod.obligatoire && <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: 6 }}>optionnel</span>}
                            </td>
                            <td><span className="gss-badge" style={{ background: mod.type === 'theorique' ? '#e3f2fd' : '#f3e5f5', color: mod.type === 'theorique' ? '#1565c0' : '#7b1fa2' }}>{mod.type}</span></td>
                            <td>
                              {fa?.validee ? (
                                <span className="gss-badge gss-badge-green">Valide</span>
                              ) : fa ? (
                                <span className="gss-badge" style={{ background: '#fff3e0', color: '#f57c00' }}>En cours</span>
                              ) : (
                                <span className="gss-badge gss-badge-grey">Non assigne</span>
                              )}
                            </td>
                            <td>{fa?.date_validation?.split('T')[0] || '-'}</td>
                            <td>{fa?.validee_par || '-'}</td>
                            <td>
                              {!fa && <button className="gss-btn-sm" onClick={() => handleAssignModule(mod.id)}>Assigner</button>}
                              {fa && !fa.validee && <button className="gss-btn-sm" style={{ background: '#e8f5e9', color: '#2e7d32', borderColor: '#2e7d32' }} onClick={() => handleValiderModule(fa.formation_id || fa.id)}>Valider</button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>Aucun module disponible</p>
              )}
            </div>

            <div style={{ padding: '0 1.5rem 1rem' }}>
              <IAAnalysisPanel type="agent" buttonLabel="Evaluation IA" data={{ agent_id: detail.id }} />
            </div>

            <div className="gss-modal-actions">
              <button className="gss-btn" onClick={() => setDetail(null)}>Fermer</button>
              <button className="gss-btn gss-btn-primary" onClick={() => handleEdit(detail)}>Modifier</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FORMULAIRE CREATION/MODIFICATION ── */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={closeForm}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  {/* ⚠️ LA BRANCHE « Nouvel agent » EST DESORMAIS INATTEIGNABLE
                    *   ICI : depuis le 16/09/2026, la seule ouverture de ce
                    *   formulaire est l EDITION (`setEditId(agent.id)`). Elle
                    *   est conservee telle quelle — le lot ne touche PAS au
                    *   formulaire (decision de Dom). ⓘ Si un bouton de creation
                    *   revenait un jour dans les Operations, ce titre le
                    *   suivrait sans bruit : c est la raison de cette note. */}
                  <h2>{editId ? 'Modifier l\'agent' : 'Nouvel agent'}</h2>
                </div>
                <div className="gss-modal-subtitle">Fiche agent de sécurité</div>
              </div>
              <button className="gss-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                {/* Section: Identite */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>IDENTITÉ DE L'AGENT</span>
                </div>

                {/* Photo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f5f5f5', border: '2px dashed var(--gss-border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {photoPreview ? (
                      photoPreview.startsWith('blob:') ? (
                        // fichier tout juste choisi : deja local, aucun jeton requis
                        <img src={photoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <ImageProtegee src={photoPreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" style={{ width: 32 }}>
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <label className="gss-btn gss-btn-primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                      {photoPreview ? 'Changer la photo' : 'Ajouter photo *'}
                      <input type="file" accept="image/jpeg,image/png" onChange={handlePhotoChange} style={{ display: 'none' }} />
                    </label>
                    <p style={{ fontSize: '0.72rem', color: 'var(--gss-text-light)', marginTop: '0.3rem' }}>JPG/PNG, max 2 MB</p>
                  </div>
                </div>

                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Nom *</label>
                    <input className="gss-input" required value={form.nom} onChange={e => setField('nom', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Prénom *</label>
                    <input className="gss-input" required value={form.prenom} onChange={e => setField('prenom', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Date de naissance *</label>
                    <input className="gss-input" type="date" required value={form.date_naissance} onChange={e => setField('date_naissance', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Sexe</label>
                    <select className="gss-input" value={form.sexe} onChange={e => setField('sexe', e.target.value)}>
                      <option value="">--</option><option value="M">Masculin</option><option value="F">Feminin</option>
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Téléphone *</label>
                    <input className="gss-input" required value={form.telephone} onChange={e => setField('telephone', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Email</label>
                    <input className="gss-input" type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Spécialité *</label>
                    <input className="gss-input" required value={form.specialite} onChange={e => setField('specialite', e.target.value)} placeholder="Ex: Gardiennage, Maitre-chien..." />
                  </div>
                  <div className="gss-field">
                    <label>Ville</label>
                    <input className="gss-input" value={form.ville} onChange={e => setField('ville', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Adresse</label>
                    <input className="gss-input" value={form.adresse} onChange={e => setField('adresse', e.target.value)} />
                  </div>

                </div>

                {/* Section: Contrat & Remuneration */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  <span>CONTRAT & RÉMUNÉRATION</span>
                </div>

                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Type de contrat *</label>
                    <select className="gss-input" required value={form.type_contrat} onChange={e => setField('type_contrat', e.target.value)}>
                      <option value="Periode_essai">Période d'essai</option>
                      <option value="CDD">CDD</option>
                      <option value="CDI">CDI</option>
                      <option value="Interim">Interim</option>
                      <option value="Stage">Stage</option>
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Date d'embauche *</label>
                    <input className="gss-input" type="date" required value={form.date_embauche} onChange={e => setField('date_embauche', e.target.value)} />
                  </div>

                  {showEssai && (
                    <>
                      <div className="gss-field">
                        <label>Fin période d'essai</label>
                        <input className="gss-input" type="date" value={form.date_fin_essai} onChange={e => setField('date_fin_essai', e.target.value)} />
                      </div>
                      <div className="gss-field" style={{ display: 'flex', alignItems: 'end', gap: '0.5rem', paddingBottom: '0.55rem' }}>
                        <input type="checkbox" checked={form.essai_renouvele} onChange={e => setField('essai_renouvele', e.target.checked)} id="essai_renouv" />
                        <label htmlFor="essai_renouv" style={{ fontSize: '0.85rem', marginBottom: 0 }}>Essai renouvelé (1 mois supp.)</label>
                      </div>
                    </>
                  )}

                  {showCDD && (
                    <div className="gss-field">
                      <label>Date fin CDD *</label>
                      <input className="gss-input" type="date" required value={form.date_fin_contrat} onChange={e => setField('date_fin_contrat', e.target.value)} />
                    </div>
                  )}

                  <div className="gss-field">
                    <label>Salaire de base *</label>
                    <input className="gss-input" type="number" required value={form.salaire_base} onChange={e => setField('salaire_base', e.target.value)} />
                  </div>

                </div>

                {/* Section: Contact d'urgence */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  <span>CONTACT D'URGENCE</span>
                </div>

                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Contact urgence (nom)</label>
                    <input className="gss-input" value={form.contact_urgence_nom} onChange={e => setField('contact_urgence_nom', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Contact urgence (tel)</label>
                    <input className="gss-input" value={form.contact_urgence_tel} onChange={e => setField('contact_urgence_tel', e.target.value)} />
                  </div>

                  {editId && (
                    <div className="gss-field">
                      <label>Statut</label>
                      <select className="gss-input" value={form.statut} onChange={e => setField('statut', e.target.value)}>
                        <option value="actif">Actif</option><option value="suspendu">Suspendu</option>
                        <option value="en_conge">En conge</option><option value="inactif">Inactif</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={closeForm}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      {editId ? 'Mettre a jour' : 'Enregistrer l\'agent'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
        * ENREGISTRER UNE SORTIE — remplace la suppression (file 100)
        * ══════════════════════════════════════════════════════════════════════
        * ⚠️ TOUS LES TEXTES DE CETTE MODALE SONT EN FRANCAIS CORRECT, ACCENTS ET
        *    APOSTROPHES COMPRIS (regle 81). Un texte vu par un utilisateur
        *    s ecrit correctement, meme quand le code alentour est sans accents.
        */}
      {sortie && (
        <div className="gss-modal-overlay" onClick={() => setSortie(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>Enregistrer une sortie</h2>
                <div className="gss-modal-subtitle">
                  {sortie.prenom} {sortie.nom}{sortie.matricule ? ` — ${sortie.matricule}` : ''}
                </div>
              </div>
              <button className="gss-modal-close" onClick={() => setSortie(null)}>&times;</button>
            </div>
            <form onSubmit={enregistrerSortie}>
              <div className="gss-form-grid">
                <div>
                  <label>Date de sortie</label>
                  <input type="date" required value={sortieForm.date_sortie}
                         onChange={e => setSortieForm(f => ({ ...f, date_sortie: e.target.value }))} />
                </div>
                <div>
                  <label>Motif de la sortie</label>
                  <select required value={sortieForm.motif_sortie_id}
                          onChange={e => setSortieForm(f => ({ ...f, motif_sortie_id: e.target.value }))}>
                    <option value="">— Choisir un motif —</option>
                    {motifs.map(m => (
                      <option key={m.code} value={m.code}>
                        {m.libelle}{m.exige_procedure ? ' — exige une procédure formelle' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* La fiche n est pas supprimee, et l ecran le DIT : sans cette
                * phrase, « sortie » se lit comme « effacement ». */}
              <div className="gss-alert" style={{ background: '#eef4fb', border: '1px solid #c5d9ee', color: '#2c4a6b' }}>
                La fiche est conservée. La personne sort de l’effectif compté et facturé
                à compter de cette date ; son historique de paie et ses attestations
                restent accessibles.
              </div>
              {sortieErreur && (
                <div className="gss-alert gss-alert-error">{sortieErreur}</div>
              )}
              {sortieAlerte && (
                <div className="gss-alert" style={{ background: '#fff4e5', border: '1px solid #f0c48a', color: '#7a4b12' }}>
                  {sortieAlerte}
                </div>
              )}
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setSortie(null)}>
                  {sortieAlerte ? 'Fermer' : 'Annuler'}
                </button>
                {!sortieAlerte && (
                  <button type="submit" className="gss-btn-deploy"
                          disabled={sortieEnCours || motifs.length === 0}>
                    {sortieEnCours ? 'Enregistrement…' : 'Enregistrer la sortie'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
        * CORRIGER LA SORTIE — on ne reecrit pas, on corrige et on garde la trace
        * ══════════════════════════════════════════════════════════════════════
        * ⚠️ Textes en francais correct, accents et apostrophes compris (regle 81).
        */}
      {correction && (
        <div className="gss-modal-overlay" onClick={() => setCorrection(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>Corriger la sortie</h2>
                <div className="gss-modal-subtitle">
                  {correction.prenom} {correction.nom}
                  {correction.matricule ? ` — ${correction.matricule}` : ''}
                  {correction.date_sortie ? ` · sortie enregistrée le ${jourFr(correction.date_sortie)}` : ''}
                </div>
              </div>
              <button className="gss-modal-close" onClick={() => setCorrection(null)}>&times;</button>
            </div>
            <form onSubmit={enregistrerCorrection}>
              <div className="gss-form-grid">
                <div>
                  <label>Que faut-il faire ?</label>
                  <select value={corrForm.mode}
                          onChange={e => setCorrForm(f => ({ ...f, mode: e.target.value }))}>
                    <option value="modifier">Modifier la date ou le motif</option>
                    <option value="annuler">Annuler la sortie — l’agent redevient présent</option>
                  </select>
                </div>
                {corrForm.mode === 'modifier' && (
                  <>
                    <div>
                      <label>Nouvelle date de sortie</label>
                      <input type="date" required value={corrForm.date_sortie}
                             onChange={e => setCorrForm(f => ({ ...f, date_sortie: e.target.value }))} />
                    </div>
                    <div>
                      <label>Motif de la sortie</label>
                      <select required value={corrForm.motif_sortie_id}
                              onChange={e => setCorrForm(f => ({ ...f, motif_sortie_id: e.target.value }))}>
                        <option value="">— Choisir un motif —</option>
                        {motifs.map(m => (
                          <option key={m.code} value={m.code}>
                            {m.libelle}{m.exige_procedure ? ' — exige une procédure formelle' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              {/* ⚠️ EXIGE, ET DISTINCT DU MOTIF DE SORTIE. L ecran le dit, pour
                * que personne n y recopie « démission ». */}
              <div style={{ marginTop: '0.75rem' }}>
                <label>Motif de la correction <span style={{ color: 'var(--gss-red)' }}>*</span></label>
                <input type="text" required maxLength={300}
                       placeholder="Pourquoi cette trace change — erreur de saisie, date rectifiée par le DG…"
                       value={corrForm.motif_correction}
                       onChange={e => setCorrForm(f => ({ ...f, motif_correction: e.target.value }))} />
                <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #666)', marginTop: 4 }}>
                  Il ne remplace pas le motif de sortie : celui-ci dit pourquoi la personne est
                  partie, celui-là pourquoi l’enregistrement change. L’ancienne valeur est conservée.
                </div>
              </div>
              <div className="gss-alert" style={{ background: '#eef4fb', border: '1px solid #c5d9ee', color: '#2c4a6b' }}>
                La fiche n’est jamais supprimée et l’ancienne valeur reste consultable dans le
                journal. Un bulletin déjà validé n’est jamais recalculé.
              </div>
              {corrErreur && <div className="gss-alert gss-alert-error">{corrErreur}</div>}
              {corrAlerte && (
                <div className="gss-alert" style={{ background: '#fff4e5', border: '1px solid #f0c48a', color: '#7a4b12' }}>
                  {corrAlerte}
                </div>
              )}
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setCorrection(null)}>
                  {corrAlerte ? 'Fermer' : 'Annuler'}
                </button>
                {!corrAlerte && (
                  <button type="submit" className="gss-btn-deploy"
                          disabled={corrEnCours || (corrForm.mode === 'modifier' && motifs.length === 0)}>
                    {corrEnCours ? 'Enregistrement…' : 'Enregistrer la correction'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
